import React, { useState, useEffect, lazy, Suspense } from 'react';
import { fetchSession } from './config/api';
import { BrowserRouter, StaticRouter, Routes, Route, useLocation } from 'react-router-dom';
import Home from './pages/NewHome';
import Login from './pages/NewLogin';
import Signup from './pages/NewSignup';
import Plans from './pages/NewPlans';
import PrivacyPolicy from './pages/NewPrivacyPolicy';
import TermsOfService from './pages/NewTermsOfService';
import Blog from './pages/NewBlog';
import BlogPost from './pages/NewBlogPost';
import NotFound from './pages/NotFound';
import Logo from './components/Logo';
import Metadados from './components/Metadados';
import AvisoDeCookies from './components/AvisoDeCookies';
import { Navigate, ProvedorDeIdioma, useIdioma } from './i18n';
import { PREFIXOS, comPrefixo } from './config/seo';

/* O que o Google indexa vai no bundle principal; a área logada e os fluxos de
   conta descem sob demanda, para a home não pagar pelos dashboards. */
const UserHome = lazy(() => import('./pages/NewUserHome'));
const UserSettings = lazy(() => import('./pages/NewUserSettings'));
const CreateProject = lazy(() => import('./pages/NewCreateProject'));
const CrawlConfig = lazy(() => import('./pages/NewCrawlConfig'));
const YouTubeDashboard = lazy(() => import('./pages/NewYouTubeDashboard'));
const SiteDashboard = lazy(() => import('./pages/NewSiteDashboard'));
const Projects = lazy(() => import('./pages/NewProjects'));
const ProjectRuns = lazy(() => import('./pages/NewProjectRuns'));
const ProjectReport = lazy(() => import('./pages/ProjectReport'));
const ForgotPassword = lazy(() => import('./pages/NewForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AceitarConvite = lazy(() => import('./pages/AceitarConvite'));
const CompleteSignup = lazy(() => import('./pages/NewCompleteSignup'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Payments = lazy(() => import('./pages/NewPayments'));
const Optimizations = lazy(() => import('./pages/NewOptimizations'));
const RunAnalysis = lazy(() => import('./pages/NewRunAnalysis'));
const ProjectIntegrations = lazy(() => import('./pages/NewProjectIntegrations'));
const Docs = lazy(() => import('./pages/NewDocs'));
// Painel interno: fora do menu, e quem decide o acesso é a API (ver pages/Admin).
const Admin = lazy(() => import('./pages/Admin'));

/* Primeiro Tab de toda página. Leva o foco ao <main>, que toda tela tem, sem
   pôr `#conteudo` na URL nem pedir um id em cada página. */
const PularParaConteudo = () => {
  const { t } = useIdioma();
  const pular = (e) => {
    const main = document.querySelector('main');
    if (!main) return;
    e.preventDefault();
    main.tabIndex = -1;
    main.focus();
  };
  return (
    <a href="#conteudo" className="sp-pular" onClick={pular}>
      {t('comum.pularConteudo')}
    </a>
  );
};

/* O router troca de página mas mantém a rolagem: o Blog do rodapé abria o Blog
   já no rodapé. Só o caminho conta, para filtro e aba na `?busca` não pularem
   ao topo; com âncora (`/#pricing`) quem rola é a página. `instant` porque o
   `html` rola com `smooth`. */
const RolarAoTopo = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
};

/* `rota` só vem do pré-render (src/prerender.js): sem navegador, quem diz a
   URL é o build. */
const App = ({ rota }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Recupera a sessão perguntando ao servidor, não ao localStorage: o cookie de
  // sessão é httpOnly, então esta é a única forma de saber se há sessão. Efeito
  // colateral bem-vindo: plano e uso vêm sempre atuais, sem cache defasado.
  useEffect(() => {
    let ativo = true;

    fetchSession().then((userData) => {
      if (!ativo) return;
      if (userData) setUser(userData);
      setLoading(false);
    });

    // Evita setState depois que o componente saiu (StrictMode monta duas vezes).
    return () => {
      ativo = false;
    };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const updateUser = (updatedData) => {
    setUser((prevUser) => (prevUser ? { ...prevUser, ...updatedData } : null));
  };

  /* A espera é inevitável onde a tela depende da sessão: montar rota
     protegida antes de ela responder faria quem tem sessão piscar no /login.
     Mas ela precisa parecer o produto — era um `<p>Loading...</p>` cru, e quem
     chegava sem sessão via um fundo branco com texto solto e concluía que
     estava quebrado. Sem texto de propósito: aqui ainda não há idioma
     resolvido. As páginas públicas não esperam: a API não pode atrasar a
     primeira pintura de quem só chegou, e o HTML pré-renderizado delas viraria
     página → carregando → página. */
  const carregando = (
    <div className="sp-boot" role="status" aria-label="Silent Puma">
      <span className="sp-boot-marca">
        <Logo size={30} />
      </span>
      <span className="sp-boot-barra">
        <span />
      </span>
    </div>
  );
  const esperar = (pagina) => (loading ? carregando : pagina);
  const Roteador = rota ? StaticRouter : BrowserRouter;
  const privada = (pagina) => esperar(user ? pagina : <Navigate to="/login" />);
  /* Página pública: a mesma tela em `/plans`, `/pt-br/plans` e `/es/plans`. */
  const publica = (caminho, pagina) =>
    Object.keys(PREFIXOS).map((idioma) => (
      <Route key={idioma} path={comPrefixo(idioma, caminho)} element={pagina} />
    ));

  return (
    <Roteador location={rota}>
      {/* A conta decide o idioma da área logada, para acompanhar a pessoa entre
          dispositivos; nas páginas públicas quem decide é a URL. */}
      <ProvedorDeIdioma idiomaDaConta={user?.language}>
        <PularParaConteudo />
        <RolarAoTopo />
        <Metadados />
        <AvisoDeCookies />
        <Suspense fallback={carregando}>
          <Routes>
            {publica('/', <Home />)}
            {publica('/login', <Login setUser={handleLogin} />)}
            {publica('/signup', <Signup setUser={handleLogin} />)}
            <Route path="/user-home" element={privada(<UserHome user={user} />)} />
            <Route path="/projects" element={privada(<Projects user={user} />)} />
            <Route
              path="/settings"
              element={privada(<UserSettings user={user} updateUser={updateUser} />)}
            />
            {/* Antes eram rotas abertas, protegidas só por uma checagem de token
              dentro da própria página. Com a sessão em cookie essa checagem
              deixou de ser possível, então passam a usar a mesma guarda das
              demais — que além de tudo é verificada pelo servidor. */}
            <Route
              path="/youtube-dashboard/:projectId"
              element={privada(<YouTubeDashboard user={user} />)}
            />
            <Route
              path="/site-dashboard/:projectId"
              element={privada(<SiteDashboard user={user} />)}
            />
            <Route path="/create-project" element={privada(<CreateProject user={user} />)} />
            <Route path="/crawl-config/:projectId" element={privada(<CrawlConfig user={user} />)} />
            <Route path="/project/:projectId/runs" element={privada(<ProjectRuns user={user} />)} />
            {/* Fora do AppShell de propósito: é um documento para imprimir, e a
              moldura fixa do app atrapalha a paginação. */}
            <Route path="/project/:projectId/report" element={privada(<ProjectReport user={user} />)} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/convite/:token" element={<AceitarConvite setUser={handleLogin} />} />
            {/* Pública de propósito: é o destino de "See plans" na home. Recebe
              `user` para que quem já é cliente veja o plano vigente marcado em
              vez de quatro botões "Get Started" iguais. Não espera a sessão:
              quem está logado e abre a URL direto vê a moldura pública por um
              instante, preço aceito para a página sair pronta no HTML. */}
            {publica('/plans', <Plans user={user} />)}
            <Route path="/complete-signup" element={<CompleteSignup setUser={setUser} />} />
            <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
            <Route path="/payments" element={privada(<Payments user={user} />)} />
            {/* Públicas — o rodapé é obrigado a linká-las — mas recebem `user`: o
              menu lateral do app também aponta para cá, e sem a moldura a pessoa
              caía numa página sem nenhuma saída. Mesma troca de /plans. */}
            {publica('/privacy-policy', <PrivacyPolicy user={user} />)}
            {publica('/terms-of-service', <TermsOfService user={user} />)}
            {/* Só na área logada, por decisão de produto: o que está escrito lá
                só faz sentido para quem já tem conta e projeto. O cabeçalho e o
                rodapé públicos linkavam `/docs` sem que a rota existisse. */}
            <Route path="/docs" element={privada(<Docs user={user} />)} />
            {/* Públicas: o rodapé linka, e o post é o tipo de página que se
                compartilha por fora. `user` só decide a moldura do 404 quando o
                slug não existe. */}
            {publica('/blog', <Blog />)}
            {publica('/blog/:slug', <BlogPost user={user} />)}
            <Route path="/optimizations/:runId" element={privada(<Optimizations user={user} />)} />
            <Route path="/run/:runId" element={privada(<RunAnalysis user={user} />)} />
            <Route
              path="/project/:projectId/integrations"
              element={privada(<ProjectIntegrations user={user} />)}
            />
            <Route path="/admin" element={privada(<Admin user={user} />)} />
            {/* Curinga por último: sem ele, qualquer caminho desconhecido não
              casava com rota alguma e o React Router renderizava nada — tela em
              branco, indistinguível de falha de carregamento. */}
            <Route path="*" element={esperar(<NotFound user={user} />)} />
          </Routes>
        </Suspense>
      </ProvedorDeIdioma>
    </Roteador>
  );
};

export default App;
